import CloseIcon from '@mui/icons-material/Close'
import { alpha, Grid2, IconButton, styled } from '@mui/material'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { find, includes, map } from 'lodash'
import { FC, useState } from 'react'
import { FileUploader as DnDFileUploader } from 'react-drag-drop-files'
import { useParams } from 'react-router-dom'
import { ReplyContentType } from '../../../api/gen/base'
import ArrowDownIcon from '../../../assets/icons/ArrowDown.svg?react'
import PaperclipIcon from '../../../assets/icons/Paperclip.svg'
import { DocumentIconName } from '../../../components/DocumentIconName'
import { REQUEST_FILE_TYPES } from '../../../constants/constants'
import { useDocumentDragState } from '../../../hooks/useDocumentDragState'
import { useWrapperStore } from '../../../store/storeWrapper'
import { getFileType } from '../../../utils/files'
import { Notistack } from '../../Notistack'
import { RegularRequestFormFile } from '../RegularRequestForm'

const DropZoneInfo = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 24,
    borderRadius: 24,
    border: '1px solid #E7E8EE',
    background: '#fff',
    height: 196,
    flexBasis: 398,
}))
const FilesWrapper = styled(Grid2)(({ theme }) => ({
    gap: '4px',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}))

const mapContentType: Partial<Record<ReplyContentType, string>> = {
    IMAGE: 'Изображений',
    VIDEO: 'Видео',
    AUDIO_VOICE: 'Записей',
    AUDIO: 'Аудиофайлов',
    DOCUMENT: 'Документов',
}

type Props = {
    files: RegularRequestFormFile[]
    onChange: (list: RegularRequestFormFile[]) => void
    onError?: (errors: string[] | null) => void
    disabled?: boolean
}

const getAttachedFileName = (file: RegularRequestFormFile) => {
    if ('name' in file) {
        return file.name
    }

    let url = ''

    if ('image_url' in file) {
        url = file.image_url ?? ''
    } else if ('video_url' in file) {
        url = file.video_url ?? ''
    } else if ('audio_url' in file) {
        url = file.audio_url ?? ''
    } else if ('file_url' in file) {
        url = file.file_url ?? ''
    }

    return url.split('/').at(-1) ?? ''
}

export const FileUploader: FC<Props> = ({ files, onChange, onError, disabled }) => {
    const { modelDetail } = useWrapperStore((state) => state)

    const { isDragging, onDrop } = useDocumentDragState()
    const params = useParams()

    const [errors, setErrors] = useState<string[] | null>(null)

    const handleResetErrors = () => {
        setErrors(null)
    }

    const handleChangeFiles = (newFiles: FileList) => {
        const selectedFiles = [...files, ...newFiles]

        const errorMessage: string[] = []
        const draftFiles: RegularRequestFormFile[] = []

        const supportedContent = modelDetail?.supported_content ?? []

        const getAvailableFileDetails = (type: string) => {
            return find(
                supportedContent,
                (content) => content.is_supported && includes(content.available_extensions, type),
            )
        }

        const draftMaxFileCountByType: Partial<Record<ReplyContentType, number>> = {}
        const availableExtensionsIsRequired: Partial<Record<ReplyContentType, boolean>> =
            supportedContent.reduce(
                (acc, { content_type, is_required }) => ({
                    ...acc,
                    [content_type]: !!is_required,
                }),
                {},
            )

        map(selectedFiles, (file) => {
            const fileName = getAttachedFileName(file)
            const type = getFileType(fileName).toLowerCase()

            const availableFileDetailsByType = getAvailableFileDetails(type)

            if (!availableFileDetailsByType) {
                return
            }

            const maxFileSize = availableFileDetailsByType.file_size
            const contentType = availableFileDetailsByType.content_type
            const maxFileCount = availableFileDetailsByType.max_file_count ?? 0

            let typeCount = draftMaxFileCountByType[contentType] ?? maxFileCount

            if (maxFileSize && 'size' in file && file.size > maxFileSize) {
                errorMessage.push(
                    `Превышен допустимый размер файла в ${maxFileSize}Б, ${file.name.length > 30 ? file.name.substring(0, 27) + '...' : file.name} не был загружен`,
                )
            } else {
                typeCount -= 1

                draftMaxFileCountByType[contentType] = typeCount

                if (typeCount < 0) {
                    errorMessage.push(
                        `Превышено допустимое количество файлов для формата ${mapContentType[contentType]} в ${availableFileDetailsByType.max_file_count},  ${fileName} не был загружен`,
                    )
                } else {
                    availableExtensionsIsRequired[contentType] = false
                    draftFiles.push(file)
                }
            }
        })

        supportedContent.forEach(({ content_type }) => {
            if (availableExtensionsIsRequired[content_type]) {
                errorMessage.push(
                    `Для корректной работы выбранной модели необходимо приложить файл в запросе ${mapContentType[content_type]}`,
                )
            }
        })

        onChange(draftFiles)

        const messages = errorMessage.length > 0 ? errorMessage : null
        setErrors(messages)
        onError?.(messages)
    }

    const handleAddFiles = () => {
        document.getElementById('file-uploader-attach-context')?.click()
    }

    const handleTypeError = () => {
        onDrop()
        onError?.(['Данные типы файлов не поддерживаются ARHIASSISTANT'])
    }

    const handleSizeError = () => {
        onDrop()
        onError?.(['Размер файла должен быть не больше 10Мб'])
    }

    const handleDraggingStateChange = (dragging: boolean) => {
        if (!dragging) {
            onDrop()
        }
    }

    const handleDeleteFile = (index: number) => () => {
        onChange(files.filter((_, idx) => idx !== index))
    }

    return (
        <>
            <Backdrop
                open={isDragging}
                sx={{
                    position: 'absolute',
                    backgroundColor: alpha('#fff', 0.85),
                    zIndex: 1001,
                }}>
                <DnDFileUploader
                    classes="file-uploader-attach-context"
                    onSizeError={handleSizeError}
                    onTypeError={handleTypeError}
                    onDraggingStateChange={handleDraggingStateChange}
                    onDrop={onDrop}
                    disabled={disabled}
                    maxSize={params.requestId ? 10 : null}
                    multiple={true}
                    types={REQUEST_FILE_TYPES}
                    handleChange={handleChangeFiles}
                    name="file-uploader-attach-context"
                    dropMessageStyle={{ display: 'none' }}>
                    <DropZoneInfo>
                        <ArrowDownIcon />
                        <Typography
                            variant="caption"
                            color="textPrimary"
                            fontWeight={500}
                            textAlign="center">
                            Перетяните файл в эту область
                        </Typography>
                    </DropZoneInfo>
                </DnDFileUploader>
            </Backdrop>
            <Button
                onClick={handleAddFiles}
                startIcon={<PaperclipIcon />}
                variant="outlined"
                color="primary"
                disabled={disabled}
                sx={{
                    display: 'flex',
                    my: 0,
                    zIndex: 1000,
                }}>
                Прикрепить вложение
            </Button>
            {!!files?.length && (
                <FilesWrapper container>
                    {map(files, (file, index) => {
                        const fileName = getAttachedFileName(file)

                        return (
                            <DocumentIconName key={`${fileName}-${index}`} name={fileName}>
                                <IconButton
                                    size="large"
                                    aria-label="delete"
                                    color="primary"
                                    disabled={disabled}
                                    onClick={handleDeleteFile(index)}>
                                    <CloseIcon />
                                </IconButton>
                            </DocumentIconName>
                        )
                    })}
                </FilesWrapper>
            )}
            <Notistack
                isError
                open={!!errors}
                message={
                    <>
                        {errors?.map((err) => (
                            <Typography variant="body2">{err}</Typography>
                        ))}
                    </>
                }
                onClose={handleResetErrors}
            />
        </>
    )
}
